import { useEffect, useState } from 'react';
import { ChevronRight, ExternalLink, KeyRound, ShieldAlert, Smartphone, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getSavedCustomerLoginPhone,
  saveCustomerLoginPhone,
} from '../../utils/login/savedPhone';
import { loginCustomerWithPassword } from '../../utils/login/passwordAuth';
import camiyuiLogo from '../../assets/camiyui-logo.jpg';
import InstallPrompt from '../../components/InstallPrompt';
import LoginNotificationPrompt from '../../components/LoginNotificationPrompt';
import { STORE_DISPLAY_NAME } from '../../constants/branding';
import { syncPushSubscriptionForCustomer } from '../../utils/pushNotifications';
import { saveCustomerAuthToken } from '../../utils/auth/customerAuthToken';
import {
  fetchStoresForReserve,
  type ReserveStore,
} from '../../utils/reservation/by-Date/fetchStoresForReserve';
import {
  fetchCustomerAppStatus,
  type PublicAppStatus,
} from '../../utils/login/appStatus';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import PasswordInput from '../../components/PasswordInput';
import { useI18n } from '../../i18n';

const CUSTOMER_LOGIN_MAINTENANCE_ENABLED = false;//ログイン不可set true, ログイン可set false

const TelegramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path
      fill="currentColor"
      d="M21.4 4.6c.4-.2.8.2.7.7l-3 14.14c-.1.46-.63.69-1.03.46l-4.83-2.81-2.47 2.38c-.35.34-.95.1-.95-.39v-3.49l8.22-7.42c.28-.25-.05-.68-.37-.46l-9.96 6.28-4.38-1.37c-.5-.16-.54-.86-.06-1.08L21.4 4.6Z"
    />
  </svg>
);

const LoginPage = () => {
  const { t } = useI18n();
  const [tel, setTel] = useState(() => getSavedCustomerLoginPhone());
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [storeContacts, setStoreContacts] = useState<ReserveStore[]>([]);
  const [appStatus, setAppStatus] = useState<PublicAppStatus | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const navigate = useNavigate();
  const appUnavailable = Boolean(appStatus && !appStatus.isAvailable);
  const loginDisabled = CUSTOMER_LOGIN_MAINTENANCE_ENABLED || appUnavailable;

  useEffect(() => {
    saveCustomerLoginPhone(tel);
  }, [tel]);

  useEffect(() => {
    const loadStatus = async (): Promise<void> => {
      try {
        setAppStatus(await fetchCustomerAppStatus());
      } catch (error) {
        console.error('Failed to load customer app status:', error);
        setAppStatus(null);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    void loadStatus();
  }, []);

  useEffect(() => {
    if (!CUSTOMER_LOGIN_MAINTENANCE_ENABLED) return;

    const loadStoreContacts = async (): Promise<void> => {
      try {
        const stores = await fetchStoresForReserve();
        setStoreContacts(stores.filter((store) => Boolean(store.telegram_url)));
      } catch (error) {
        console.error(error);
      }
    };

    void loadStoreContacts();
  }, []);

  const finalizeLogin = async (customerId: number, name: string, authToken: string) => {
    saveCustomerAuthToken(authToken);
    localStorage.setItem('customerId', String(customerId));
    localStorage.setItem('customerName', name);
    void syncPushSubscriptionForCustomer(customerId).catch((error) => {
      console.error('Failed to sync push subscription after login:', error);
    });

    toast.success(t('login.welcome', { name }));
    navigate('/reserve');
  };

  const handleLogin = async () => {
    if (appUnavailable) {
      toast.info(appStatus?.message || t('login.unavailableToast'));
      return;
    }

    if (CUSTOMER_LOGIN_MAINTENANCE_ENABLED) {
      toast.info(t('login.maintenanceShort'));
      return;
    }

    const normalizedTel = tel.trim();

    if (!normalizedTel) {
      toast.warning(t('login.enterPhone'));
      return;
    }

    if (!password) {
      toast.warning(t('login.enterPassword'));
      return;
    }

    if (normalizedTel !== tel) {
      setTel(normalizedTel);
    }

    setIsSubmitting(true);
    try {
      const result = await loginCustomerWithPassword(normalizedTel, password);
      saveCustomerLoginPhone(normalizedTel);

      if (!result.ok) {
        if (result.status === 404 || result.status === 409 || result.status === 401) {
          toast.info(result.message);
          return;
        }
        toast.error(result.message);
        return;
      }

      const { customer_id, name, auth_token } = result.data;
      await finalizeLogin(customer_id, name, auth_token);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#e8f5f1_0%,#f8fafc_42%,#eef5f8_100%)] px-4 py-8">
      <LoginNotificationPrompt />

      <main className="mx-auto max-w-md">
        <div className="mb-2 flex justify-end">
          <LanguageSwitcher />
        </div>

        <section className="mb-5 px-2 pt-3 pb-1">
          <div className="flex items-center justify-center gap-3">
            <div className="h-14 w-14 overflow-hidden rounded-[18px] border border-white/70 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <img
                src={camiyuiLogo}
                alt={STORE_DISPLAY_NAME}
                className="h-full w-full object-cover"
              />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                {STORE_DISPLAY_NAME}
              </p>
              <h1 className="mt-1 text-[28px] font-semibold tracking-[-0.03em] text-slate-950">
                {t('login.title')}
              </h1>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[32px] border border-emerald-100 bg-white/90 shadow-lg shadow-emerald-100/60 backdrop-blur">
          <div className="border-b border-emerald-100/80 bg-white px-5 py-5">
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-950">
              {CUSTOMER_LOGIN_MAINTENANCE_ENABLED ? t('login.comingSoon') : t('login.welcomeBack')}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              {CUSTOMER_LOGIN_MAINTENANCE_ENABLED
                ? t('login.maintenanceShort')
                : t('login.loginHelp')}
            </p>
          </div>

          <div className="space-y-5 p-5">
            {CUSTOMER_LOGIN_MAINTENANCE_ENABLED ? (
              <div className="rounded-[26px] border border-sky-100 bg-sky-50/80 px-4 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-600">
                  Coming Soon
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-700">
                  {t('login.maintenanceDetail')}
                </p>

                {storeContacts.length > 0 ? (
                  <div className="mt-4 space-y-2">
                    {storeContacts.map((store) => (
                      <a
                        key={store.id}
                        href={store.telegram_url ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        className="flex min-h-[54px] items-center justify-between gap-3 rounded-[18px] border border-sky-200 bg-white px-3 py-2 text-sky-700 transition hover:bg-sky-50"
                        aria-label={t('login.openTelegram', { store: store.name })}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-sky-500 text-white">
                            <TelegramIcon className="h-5 w-5" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-slate-900">
                              {store.name}
                            </span>
                            <span className="block text-xs font-medium text-sky-700">
                              {t('login.contactTelegram')}
                            </span>
                          </span>
                        </span>
                        <ExternalLink className="h-4 w-4 shrink-0" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-xs leading-5 text-slate-500">
                    {t('login.telegramChecking')}
                  </p>
                )}
              </div>
            ) : null}

            {!CUSTOMER_LOGIN_MAINTENANCE_ENABLED && appUnavailable ? (
              <div className="rounded-[26px] border border-amber-200 bg-amber-50 px-4 py-4 text-amber-950">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[16px] bg-amber-100 text-amber-700">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold">
                      {t('login.unavailableTitle')}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-amber-900">
                      {appStatus?.message ||
                        t('login.unavailableDefault')}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-amber-800">
                      {t('login.askStaff')}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            <fieldset
              disabled={loginDisabled}
              className={loginDisabled ? 'space-y-5 opacity-45' : 'space-y-5'}
              aria-disabled={loginDisabled}
            >
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <Smartphone className="h-4 w-4 text-emerald-700" />
                  Phone Number
                </div>
                <input
                  type="tel"
                  value={tel}
                  onChange={(e) => setTel(e.target.value)}
                  placeholder="Example: 0123456789"
                  className="w-full rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <KeyRound className="h-4 w-4 text-emerald-700" />
                  Password
                </div>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="w-full rounded-[22px] border border-slate-200 bg-slate-50 py-4 pl-4 pr-14 text-base text-slate-900 outline-none transition focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-100"
                />
                <button
                  type="button"
                  onClick={() => navigate('/login/store-setup')}
                  disabled={loginDisabled}
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-[18px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <KeyRound className="h-4 w-4" />
                  Forgot password? Enter 6-digit store code
                </button>
              </div>

              <button
                type="button"
                onClick={handleLogin}
                disabled={isSubmitting || loginDisabled || isCheckingStatus}
                className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[22px] bg-slate-950 px-4 py-4 text-base font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCheckingStatus ? 'Checking availability...' : 'Continue'}
                <ChevronRight className="h-5 w-5" />
              </button>

              <button
                type="button"
                onClick={() => navigate(`/registration${tel.trim() ? `?tel=${encodeURIComponent(tel.trim())}` : ''}`)}
                disabled={loginDisabled}
                className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[22px] border border-slate-200 bg-white px-4 py-4 text-base font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <UserPlus className="h-5 w-5" />
                New customer registration
              </button>

            </fieldset>
          </div>

          <div className="px-5 pb-5">
            <InstallPrompt mode="embedded" />
          </div>
        </section>
      </main>
    </div>
  );
};

export default LoginPage;
